/**
 * Post-submit scores and interpretation (Figma “Your Results”).
 * @agentic members
 */

import {
  Paper,
  Stack,
  Title,
  Text,
  Button,
  RingProgress,
  Badge,
  Box,
} from "@mantine/core";
import { OBSERVATION_RISK_LEVELS } from "@benchmark/core/constants";

import { SurveyWordmark } from "./SurveyWordmark";

import styles from "./ResultsScreen.module.css";

/**
 * One scored instrument for display.
 * @agentic members
 */
export type ResultScoreCard = {
  questionnaireUrl: string;
  label: string;
  total: number;
  maxScore: number;
  interpretation: string;
  severityDisplay: string;
};

type ResultsScreenProps = {
  patientDisplayName: string;
  scores: ResultScoreCard[];
  checkInNumber?: number;
  onSeeNextSteps: () => void;
};

/**
 * Mantine color per known risk-level display (e.g. "Moderate" → orange,
 * "Positive" → orange, "Negative" → green), sourced from the shared risk-level
 * palette so new instruments' severities render consistently.
 * @agentic members
 */
const RISK_LEVEL_COLOR_BY_DISPLAY: Record<string, string> = Object.fromEntries(
  Object.values(OBSERVATION_RISK_LEVELS).map((level) => [
    level.display.toLowerCase(),
    level.color,
  ]),
);

/**
 * Map a severity label to a Mantine color for visual distinction. Falls back to
 * substring matching for any unmapped label.
 * @agentic members
 */
const severityColor = (severity: string): string => {
  const key = severity.toLowerCase();
  const exact = RISK_LEVEL_COLOR_BY_DISPLAY[key];
  if (exact) return exact;
  if (key.includes("severe") || key.includes("high")) return "red";
  if (key.includes("moderate") || key.includes("positive")) return "orange";
  if (key.includes("mild") || key.includes("low")) return "yellow";
  return "green";
};

/**
 * Shows arc-style score summary and CTA to next steps.
 * @agentic members
 */
export const ResultsScreen = ({
  scores,
  checkInNumber,
  onSeeNextSteps,
}: ResultsScreenProps) => {
  const today = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const subtitle = checkInNumber
    ? `Check-in #${String(checkInNumber)} · ${today}`
    : today;

  return (
    <Paper radius="lg" p="xl" shadow="sm">
      <Stack gap="lg">
        <SurveyWordmark />
        <Stack gap={4}>
          <Title order={2} c="#391640">
            Your Results
          </Title>
          <Text size="sm" style={{ opacity: 0.5, color: "#2a1a2e" }}>
            {subtitle}
          </Text>
        </Stack>

        {scores.length === 0 && (
          <Text size="sm" c="dimmed">
            Your responses were submitted. Your care team will review them.
          </Text>
        )}

        {scores.map((s) => {
          const pct = s.maxScore > 0 ? (s.total / s.maxScore) * 100 : 0;
          const badgeColor = severityColor(s.severityDisplay);
          return (
            <Box key={s.questionnaireUrl} className={styles.resultBlock}>
              <Text
                size="xs"
                tt="uppercase"
                fw={600}
                mb="xs"
                style={{
                  letterSpacing: "0.04em",
                  opacity: 0.35,
                  color: "#2a1a2e",
                }}
              >
                {s.label}
              </Text>
              <Stack align="center" gap="sm">
                <RingProgress
                  size={140}
                  thickness={12}
                  sections={[{ value: pct, color: "plum.5" }]}
                  label={
                    <Text ta="center" fz="xl" fw={400} c="#391640">
                      {s.total}
                    </Text>
                  }
                />
                <Text size="xs" style={{ opacity: 0.4, color: "#2a1a2e" }}>
                  out of {s.maxScore}
                </Text>
                <Badge color={badgeColor} variant="light" size="lg">
                  {s.severityDisplay}
                </Badge>
                <Text
                  size="sm"
                  ta="center"
                  style={{ opacity: 0.55, color: "#2a1a2e" }}
                >
                  {s.interpretation}
                </Text>
              </Stack>
            </Box>
          );
        })}

        <Text size="sm" style={{ opacity: 0.55, color: "#2a1a2e" }}>
          These scores are one piece of the picture — not a diagnosis. They are
          most useful as a conversation starter with your care team.
        </Text>

        <Button size="lg" fullWidth onClick={onSeeNextSteps}>
          See next steps
        </Button>
      </Stack>
    </Paper>
  );
};
